"use client";

import React, { createContext, useContext, useMemo, useRef } from "react";
import { newOptimizely } from "analytics/optimizely";
import { trackExposure } from "./track/track-exposure";
import type { Client } from "@optimizely/optimizely-sdk";

type Vars = Record<string, unknown>;
export type Decision<V extends Vars = Vars> = {
  enabled: boolean;
  variation: string | null;
  variables: V;
};
type Attrs = { countryCode?: string | null; [k: string]: unknown };

type Ctx = {
  decide<V extends Vars = Vars>(
    flag: string,
    experimentKey: string,
    attrs?: Attrs
  ): Promise<Decision<V>>;
};

const ExperimentsContext = createContext<Ctx | null>(null);

let sdkPromise: Client | null = null;
async function getSdk(datafile: Record<string, unknown>) {
  if (!sdkPromise) {
    sdkPromise = await newOptimizely(datafile);
  }
  return sdkPromise;
}

function readCookie(name: string) {
  try {
    return document.cookie
      .split("; ")
      .map((c) => c.split("="))
      .reduce<Record<string, string>>((a, [k, ...v]) => {
        a[decodeURIComponent(k)] = decodeURIComponent(v.join("="));
        return a;
      }, {})[name];
  } catch {
    return;
  }
}

export function ExperimentsProvider({
  children,
  datafile,
  defaultAttrs
}: {
  children: React.ReactNode;
  datafile: Record<string, unknown>;
  defaultAttrs?: Attrs;
}) {
  const cacheRef = useRef(new Map<string, Decision>());
  const exposedRef = useRef(new Set<string>());

  const ctx = useMemo<Ctx>(
    () => ({
      async decide<V extends Vars = Vars>(
        flag: string,
        experimentKey: string,
        attrs?: Attrs
      ): Promise<Decision<V>> {
        // Generate a cache key that includes both flag and experiment key
        const cacheKey = `${flag}:${experimentKey}`;
        const hit = cacheRef.current.get(cacheKey) as Decision<V> | undefined;
        if (hit) return hit;

        const sdk = await getSdk(datafile);
        const uid = readCookie("user-id");
        const userId = uid || "anonymous";
        const merged = { ...defaultAttrs, ...attrs };

        const enabled = sdk?.isFeatureEnabled?.(flag, userId, merged) || false;

        const variation =
          sdk?.getVariation?.(experimentKey, userId, merged) || null;
        const rawVariables =
          sdk?.getAllFeatureVariables?.(flag, userId, merged) || {};
        const variables = rawVariables as V;

        const decision: Decision<V> = { enabled, variation, variables };
        cacheRef.current.set(cacheKey, decision);

        // Track exposure using the experiment key
        const variationKey =
          variation ?? (enabled ? "enabled_no_variation" : "control");

        if (!exposedRef.current.has(experimentKey)) {
          trackExposure(experimentKey, variationKey, uid ?? "anonymous");
          exposedRef.current.add(experimentKey);
        }

        return decision;
      }
    }),
    [datafile, defaultAttrs]
  );

  return (
    <ExperimentsContext.Provider value={ctx}>
      {children}
    </ExperimentsContext.Provider>
  );
}

export function useExperimentsContext() {
  const ctx = useContext(ExperimentsContext);
  if (!ctx)
    throw new Error(
      "useExperimentsContext must be used within ExperimentsProvider"
    );
  return ctx;
}
